import {
  Box,
  Button,
  Flex,
  FormControl,
  Heading,
  HStack,
  Icon,
  Image,
  Text,
  useToast,
} from "@chakra-ui/react";
import { PhoneNumberInput } from "components/PhoneNumberInput";

import AppInput from "components/AppInput";
import AppSelect from "components/AppSelect";
import AppTextArea from "components/AppTextArea";
import { useFormik } from "formik";
import { useApi } from "hooks/useApi";
import { useCache } from "hooks/useCache";
import { useGetGeoLocation } from "hooks/useGetGeoLocation";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { MdMyLocation } from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import { Store } from "store";
import { showToast, stringifyErrors, toastTypes } from "utils";
import * as Yup from "yup";
import { useUserRole } from "hooks/useUserRole";
import { TAX_COLLECTOR, TAX_SUPERVISOR, TAX_SUPER_ADMIN } from "utils/roles";

export default function ManageTaxPayerPage() {
  const { id } = useParams();
  const toast = useToast();
  const navigate = useNavigate();
  const { location, getLocation } = useGetGeoLocation()
  const [city, setCity] = useState("")
  const [country, setCountry] = useState("")
  const [institution, setInstitution] = useState("");

  // États pour la recherche de débiteur existant
  const [searchEnabled, setSearchEnabled] = useState(false); // Checkbox activée/désactivée
  const [searchValue, setSearchValue] = useState(""); // Valeur de recherche (email ou téléphone)
  const [searching, setSearching] = useState(false); // État de chargement de la recherche
  const [foundTaxPayer, setFoundTaxPayer] = useState<any>(null); // Débiteur trouvé
  const [searchError, setSearchError] = useState(""); // Message d'erreur


  const {
    dispatch,
    state: { user },
  } = useContext(Store);
  const userRole = useUserRole();
  const {
    getTaxPayer,
    addTaxPayer,
    updateTaxPayer,
    getTaxPayers, // Rechercher des dbiteurs existants
    getCountries,
    getCities,
    getAreas,
    getSubAreas,
    getInstitutions,
  } = useApi();
  const {
    data: countries,
    isLoading: isCountryLoading,
    mutate: mutateCountry,
  } = useCache("countries", () => getCountries(1, ""));
  const {
    data: taxPayer,
    isLoading,
    mutate,
  } = useCache("taxPayer", () => getTaxPayer(+id || 0));

  const [profilePictureSrc, setProfilePictureSrc] = useState(null);
  // État pour masquer/afficher les champs de mot de passe (par défaut masqués)
  const [showPasswordFields, setShowPasswordFields] = useState(false);

  const initialValues = useMemo(() => {
    return {
      email: taxPayer?.email || "",
      companyName: taxPayer?.company_name || "",
      firstName: taxPayer?.first_name || "",
      lastName: taxPayer?.last_name || "",
      lat: taxPayer?.lat || "",
      long: taxPayer?.long || "",
      city: taxPayer?.city_id || "",
      area: taxPayer?.area_id || "",
      subArea: taxPayer?.sub_area_id || "",
      country: taxPayer?.country_id || "",
      landmark: taxPayer?.landmark || "",
      phoneNumber: taxPayer?.phone_number || "",
      addressDescription: taxPayer?.description || "",
      profilePicture: taxPayer?.profile_picture || "",
      institution:
        user?.user?.institution_id || taxPayer?.institution_id || "",
      // Champs de mot de passe (mode création et modification)
      password: "",
      passwordConfirmation: "",
      submit: null,
    };
  }, [taxPayer, user?.user?.institution_id]);

  const isTaxCollector = userRole === TAX_COLLECTOR;

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().trim().required("Nom requis"),
    lastName: Yup.string().trim().required("Nom requis"),
    email: Yup.string().email().trim().required("email requis"),
    companyName: Yup.string().trim().required("Nom légal de l'entité requis"),
    phoneNumber: Yup.string().trim().required("le numéro de téléphone est requis"),
    // Validation conditionnelle : les champs de zone ne sont pas requis pour les tax collectors
    country: isTaxCollector 
      ? Yup.string().trim() 
      : Yup.string().trim().required("pays requis"),
    city: isTaxCollector 
      ? Yup.string().trim() 
      : Yup.string().trim().required("ville requis"),
    subArea: isTaxCollector 
      ? Yup.string().trim() 
      : Yup.string().trim().required("zone requis"),
    addressDescription: Yup.string().trim(),
    long: Yup.string().trim(),
    lat: Yup.string().trim(),
    landmark: Yup.string().trim(),
    // L'institution n'est pas requise pour les tax collectors (sera définie automatiquement)
    institution: isTaxCollector 
      ? Yup.string().trim() 
      : Yup.string().trim().required("institution requis"),
    profilePicture: Yup.mixed(),
    // Validation des mots de passe (mode création et modification)
    password: Yup.string()
      .min(6, "Le mot de passe doit contenir au moins 6 caractères")
      .test('password-required-if-confirmation', 'Le mot de passe est requis', function (value) {
        const { passwordConfirmation } = this.parent;
        // Si la confirmation est remplie, le mot de passe est requis
        if (passwordConfirmation && passwordConfirmation.length > 0) {
          return value && value.length > 0;
        }
        return true; // Sinon, le mot de passe est optionnel
      }),
    passwordConfirmation: Yup.string()
      .oneOf([Yup.ref('password')], "Les mots de passe doivent être identiques")
      .test('confirmation-required-if-password', 'La confirmation du mot de passe est requise', function (value) {
        const { password } = this.parent;
        // Si le mot de passe est rempli, la confirmation est requise
        if (password && password.length > 0) {
          return value && value.length > 0;
        }
        return true; // Sinon, la confirmation est optionnelle
      }),
  });

  const {
    errors,
    values,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
    setFieldValue,
  } = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        let formData: any = new FormData();
        if (profilePictureSrc) {
          formData.append(
            "profile_picture",
            values.profilePicture,
            values.profilePicture.name
          );
        }
        formData.append("first_name", values.firstName);
        formData.append("last_name", values.lastName);
        formData.append("phone_number", values.phoneNumber);
        // Pour les tax collectors, ne pas envoyer les champs de zone (seront définis automatiquement par le backend)
        if (!isTaxCollector) {
          // Pour les autres utilisateurs, envoyer les champs normalement
          if (values.country) formData.append("country_id", values.country);
          if (values.city) formData.append("city_id", values.city);
          if (values.subArea) formData.append("sub_area_id", values.subArea);
          if (values.institution) formData.append("institution_id", values.institution);
        }
        // Note: Pour les tax collectors, ces champs ne sont pas envoyés car ils seront définis automatiquement
        formData.append("company_name", values.companyName);
        formData.append("lat", values.lat);
        formData.append("long", values.long);
        formData.append("landmark", values.landmark);
        formData.append("description", values.addressDescription);
        formData.append("email", values.email);
        formData.append("is_active", true);
        // Ajouter le mot de passe s'il est saisi (mode création et modification)
        if (values.password) {
          formData.append("password", values.password);
        }
        if (id) {
          formData.append("id", id);
        }
        let response: any = null;
        if (id) {
          response = await updateTaxPayer(formData);
        } else {
          response = await addTaxPayer(formData);
        }
        if (!response.error_code && !response.errors) {
          if (response.id) {
            dispatch(
              showToast({
                toast: toast,
                message: id
                  ? "Le Débiteur a été mis à jour avec succès."
                  : "Le Débiteur a été créé avec succès.",
                status: toastTypes.success,
              })
            );
            if (id) {
              mutate(getTaxPayer(+id));
              navigate("/tax-payers");
            } else {
              navigate("/tax-payers");
            }
          }
          navigate("/tax-payers");
        } else {
          dispatch(
            showToast({
              toast: toast,
              message: response.errors
                ? stringifyErrors(response.errors)
                : response.message,
              status: toastTypes.error,
            })
          );
        }
      } catch (ex) {
        console.log(ex);
        dispatch(
          showToast({
            toast: toast,
            message: "Quelque chose s'est mal passé",
            status: toastTypes.error,
          })
        );
      }
    },
  });
  // const {
  //   data: cities,
  //   isLoading: isCityLoading,
  //   mutate: mutateCity,
  // } = useCache("cities", () => getCities("", 1, city));

  const {
    data: cities,
    isLoading: isCityLoading,
    mutate: mutateCity,
  } = useCache("cities", () => {
    // En mode édition, utiliser le pays du taxPayer si disponible
    const countryId = values.country || (id && taxPayer?.country_id ? taxPayer.country_id : "");
    return getCities(countryId || "", 1, city || "");
  });

  const {
    data: areas,
    isLoading: isAreaLoading,
    mutate: mutateArea,
  } = useCache("areas", () => getAreas(1, values.city));

  const {
    data: subAreas,
    isLoading: isSubAreaLoading,
    mutate: mutateSubArea,
  } = useCache("subAreas", () => getSubAreas(1, values.area, "", institution || user?.user?.institution_id || taxPayer?.institution_id || ""));

  const {
    data: institutions,
    isLoading: isInstitutionLoading,
    mutate: mutateInstitution,
  } = useCache("institutions", () =>
    getInstitutions(1, values.institution, "")
  );

  // À ajouter après les useCache
  useEffect(() => {
    if (id && taxPayer?.country_id && taxPayer?.city_id) {
      // En mode édition, charger les villes du pays
      getCities(String(taxPayer.country_id), 1, "")
        .then((citiesData) => {
          mutateCity(citiesData || []);
          // Définir la ville si elle existe
          if (taxPayer.city_id && !values.city) {
            setFieldValue("city", String(taxPayer.city_id));
          }
        });
    }
  }, [id, taxPayer?.country_id, taxPayer?.city_id, mutateCity, getCities, values.city, setFieldValue]);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    const url = URL.createObjectURL(file);
    setProfilePictureSrc(url);
    setFieldValue("profilePicture", file);
  };

  const handleGetLocation = useCallback(async () => {
    await getLocation()
    console.log("here")
    if (location) {
      setFieldValue("lat", location.latitude)
      setFieldValue("long", location.longitude)
    }
  }, [location, setFieldValue, getLocation]);

  // Fonction pour vérifier l'existence d'un débiteur
  const handleSearchTaxPayer = useCallback(async () => {
    if (!searchValue.trim()) {
      setSearchError("Veuillez entrer un email ou un numéro de téléphone");
      return;
    }

    setSearching(true);
    setSearchError("");
    setFoundTaxPayer(null);

    try {
      // Rechercher avec le keyword (email ou téléphone)
      const result = await getTaxPayers(1, searchValue.trim(), 1);

      if (result && result.length > 0) {
        // Un ou plusieurs débiteurs trouvés
        const taxPayer = result[0]; // Prendre le premier résultat
        setFoundTaxPayer(taxPayer);

        // Pré-remplir le formulaire avec les données du débiteur trouvé
        // IMPORTANT : Tous les champs restent éditables après le pré-remplissage
        if (taxPayer) {
          setFieldValue("email", taxPayer.email || "");
          setFieldValue("phoneNumber", taxPayer.phone_number || "");
          //setFieldValue("companyName", taxPayer.company_name || "");
          setFieldValue("firstName", taxPayer.first_name || "");
          setFieldValue("lastName", taxPayer.last_name || "");

          dispatch(
            showToast({
              toast: toast,
              message: "Débiteur trouvé. Le formulaire a été pré-rempli. Vous pouvez modifier tous les champs.",
              status: toastTypes.success,
            })
          );
        }
      } else {
        setSearchError("Aucun débiteur trouvé avec cet email ou ce numéro de téléphone");
        setFoundTaxPayer(null);
      }
    } catch (error) {
      console.error("Erreur lors de la recherche:", error);
      setSearchError("Une erreur est survenue lors de la recherche");
      setFoundTaxPayer(null);
    } finally {
      setSearching(false);
    }
  }, [searchValue, getTaxPayers, setFieldValue, mutateCity, getCities, dispatch, toast]);

  // console.log(errors, values)
  return (
    <>
      {!isLoading && (
        <Flex
          gap="18px"
          bg="very_light_gray"
          flex={1}
          flexDirection="column"
          py="20px"
          px="30px"
          minH={"100vh"}
        >
          <Box>
            <Flex gap="4px" flexDirection="column">
              <Flex justifyContent="center" alignItems="center">
                <Heading size="text2xl" as="h4" fontFamily="Arimo">
                  {id ? "Modifier Débiteur" : "Ajouter un Débiteur"}
                </Heading>
                <Flex
                  pl="56px"
                  pr="62px"
                  gap="9px"
                  flex={1}
                  justifyContent="flex-end"
                  px={{ md: 0, base: "20px" }}
                ></Flex>
              </Flex>
            </Flex>
          </Box>
          <form
            onSubmit={handleSubmit}
            style={{
              flexDirection: "column",
              margin: "0px",
              alignItems: "center",
            }}
          >
            <Flex flexDirection="column" alignItems="center">
              <Flex
                gap="16px"
                bg="white"
                boxShadow="xl"
                w="100%"
                flexDirection="column"
                px={{ base: "20px", sm: "24px" }}
                py={{ base: "20px", sm: "24px" }}
                mx={{ md: "62px", base: "0px" }}
                borderRadius="16px"
              >
                <Flex gap="8px" flexDirection="column" alignItems="start">
                  <HStack>
                    <Text color="gray_description" mt="16px">
                      Photo du Débiteur
                    </Text>
                    <span style={{ color: "red" }}>*</span>
                  </HStack>
                  <AppInput
                    type="file"
                    accept="image/*"
                    borderWidth="1px"
                    alignSelf="stretch"
                    borderRadius="8px"
                    name="profilePicture"
                    id="profilePicture"
                    onChange={(e: any) => handleFileChange(e)}
                    display="none"
                  />
                  <Flex
                    gap="16px"
                    alignSelf="stretch"
                    alignItems="center"
                    justifyContent={"flex-start"}
                    flexDirection={{ md: "row", base: "column" }}
                  >
                    <Image
                      src={
                        profilePictureSrc || taxPayer?.profile_picture
                          ? profilePictureSrc || taxPayer?.profile_picture
                          : "images/image_upload_preview.jpg"
                      }
                      alt="product Image"
                      h="100px"
                      alignSelf="center"
                      w={"100px"}
                      fit="contain"
                      borderRadius="12px"
                      onClick={() =>
                        document.getElementById("profilePicture").click()
                      }
                    />
                  </Flex>
                </Flex>
                {/* Message informatif pour les tax collectors */}
                {isTaxCollector && (
                  <Flex
                    gap="4px"
                    bg="blue.50"
                    border="1px solid"
                    borderColor="blue.200"
                    borderRadius="8px"
                    p="12px"
                    flexDirection="column"
                    alignItems="start"
                    w={"full"}
                  >
                    <Text fontSize="14px" color="blue.700" fontWeight="bold">
                      ℹ️ Information
                    </Text>
                    <Text fontSize="12px" color="blue.600">
                      En tant que collecteur, les informations de zone (pays, ville, commune, zone et institution) seront automatiquement définies selon votre zone d'affectation.
                    </Text>
                  </Flex>
                )}
                {!user?.user?.institution_id && !isTaxCollector && (
                  <Flex
                    gap="4px"
                    flexDirection="column"
                    alignItems="start"
                    w={"full"}
                  >
                    <HStack>
                      <Text color="gray_description">Institution</Text>
                      <span style={{ color: "red" }}>*</span>
                    </HStack>
                    <FormControl
                      isInvalid={
                        !!errors.institution &&
                        (touched.institution as boolean)
                      }
                    ></FormControl>
                    {!isInstitutionLoading && (
                      <AppSelect
                        id="institution"
                        name="institution"
                        onChange={(field: string, value: any) => {
                          setFieldValue(field, value);
                          setInstitution(value);
                          mutateInstitution(
                            getInstitutions(1, +values.institution, value)
                          );
                        }}
                        value={
                          values.institution || initialValues.institution
                        }
                        placeholder="Veuillez sélectionner une institution"
                        options={institutions}
                        isMulti={false}
                        isLoading={isInstitutionLoading}
                        showErrorMessage={Boolean(touched.institution && errors.institution)}
                        errorMessage={errors.institution}
                        onBlur={handleBlur}
                      />
                    )}
                  </Flex>
                )}
                <Flex
                  flexDirection={{ md: "row", base: "column" }}
                  w={"full"}
                  gap={5}
                >

                  {/* Rechercher le débiteur */}

                  {!id && (
                    <Flex
                      gap="4px"
                      bg="white"
                      boxShadow="xl"
                      w="100%"
                      flexDirection="column"
                      px={{ base: "20px", sm: "24px" }}
                      py={{ base: "20px", sm: "24px" }}
                      mx={{ md: "0px", base: "0px" }}
                      borderRadius="16px"
                      mb="18px"
                    >
                      <Flex gap="12px" flexDirection="column">
                        <Heading size="md" fontFamily="Arimo">
                          Rechercher le débiteur
                        </Heading>

                        {/* Checkbox pour activer/désactiver la recherche */}
                        <Flex alignItems="center" gap="8px">
                          <input
                            type="checkbox"
                            id="enableSearch"
                            checked={searchEnabled}
                            onChange={(e) => {
                              setSearchEnabled(e.target.checked);
                              if (!e.target.checked) {
                                // Réinitialiser si la recherche est désactivée
                                setSearchValue("");
                                setFoundTaxPayer(null);
                                setSearchError("");
                              }
                            }}
                            style={{ width: "18px", height: "18px", cursor: "pointer" }}
                          />
                          <Text
                            as="label"
                            htmlFor="enableSearch"
                            color="gray_description"
                            cursor="pointer"
                          >
                            Rechercher un débiteur existant par email ou téléphone
                          </Text>
                        </Flex>

                        {/* Champ de recherche (affiché seulement si checkbox activée) */}
                        {searchEnabled && (
                          <Flex gap="12px" flexDirection="column">
                            <Flex gap="8px" flexDirection={{ md: "row", base: "column" }}>
                              <AppInput
                                placeholder="Email ou numéro de téléphone"
                                value={searchValue}
                                onChange={(e: any) => setSearchValue(e.target.value)}
                                onKeyPress={(e: any) => {
                                  if (e.key === "Enter") {
                                    handleSearchTaxPayer();
                                  }
                                }}
                                flex={1}
                              />
                              <Button
                                onClick={handleSearchTaxPayer}
                                isLoading={searching}
                                colorScheme="blue"
                                minW="120px"
                              >
                                Rechercher
                              </Button>
                            </Flex>

                            {/* Message d'erreur */}
                            {searchError && (
                              <Text color="red" fontSize="14px">
                                {searchError}
                              </Text>
                            )}

                            {/* Affichage du débiteur trouvé */}
                            {/* {foundTaxPayer && (
                  <Flex
                    gap="8px"
                    p="12px"
                    bg="green.50"
                    borderRadius="8px"
                    border="1px solid"
                    borderColor="green.200"
                    flexDirection="column"
                  >
                    <Text fontWeight="bold" color="green.700">
                      ✓ Débiteur trouvé
                    </Text>
                    <Text fontSize="14px" color="gray.700">
                      <strong>Nom:</strong> {foundTaxPayer.first_name} {foundTaxPayer.last_name}
                    </Text>
                    <Text fontSize="14px" color="gray.700">
                      <strong>Email:</strong> {foundTaxPayer.email}
                    </Text>
                    <Text fontSize="14px" color="gray.700">
                      <strong>Téléphone:</strong> {foundTaxPayer.phone_number}
                    </Text>
                    <Text fontSize="14px" color="gray.700">
                      <strong>Entreprise:</strong> {foundTaxPayer.company_name}
                    </Text>
                    <Text fontSize="12px" color="gray.500" mt="4px">
                      Le formulaire a été pré-rempli avec les informations de ce débiteur.
                      <strong> Tous les champs restent modifiables</strong> - vous pouvez changer n'importe quelle valeur selon vos besoins.
                    </Text>
                  </Flex>
                )} */}
                          </Flex>
                        )}
                      </Flex>
                    </Flex>)
                  }

                  {/* Nom légal de l’entité */}
                  <Flex
                    gap="4px"
                    flexDirection="column"
                    alignItems="start"
                    w={"full"}
                  >
                    <HStack>
                      <Text color="gray_description">Nom légal de l’entité</Text>
                      <span style={{ color: "red" }}>*</span>
                    </HStack>
                    <FormControl
                      isInvalid={!!errors.companyName && (touched.companyName as boolean)}
                    >
                      <AppInput
                        required
                        placeholder={`Nom légal de l’entité`}
                        borderWidth="1px"
                        alignSelf="stretch"
                        borderRadius="8px"
                        id="companyName"
                        name="companyName"
                        autoComplete="companyName"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.companyName}
                        showErrorMessage={Boolean(touched.companyName && errors.companyName)}
                        errorMessage={errors.companyName}
                      />
                    </FormControl>
                  </Flex>
                </Flex>
                <Flex gap="16px" flexDirection="column">
                  <Flex
                    flexDirection={{ md: "row", base: "column" }}
                    w={"full"}
                    gap={5}
                  >
                    <Flex
                      gap="4px"
                      flexDirection="column"
                      alignItems="start"
                      w={"full"}
                    >
                      <HStack>
                        <Text color="gray_description">Nom du représentant</Text>
                        <span style={{ color: "red" }}>*</span>
                      </HStack>
                      <FormControl
                        isInvalid={
                          !!errors.lastName && (touched.lastName as boolean)
                        }
                      >
                        <AppInput
                          required
                          placeholder={`Nom`}
                          borderWidth="1px"
                          alignSelf="stretch"
                          borderRadius="8px"
                          id="firstName"
                          name="lastName"
                          autoComplete="lastName"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.lastName}
                          showErrorMessage={Boolean(
                            touched.lastName && errors.lastName
                          )}
                          errorMessage={errors.lastName}
                        />
                      </FormControl>
                    </Flex>
                    <Flex
                      gap="4px"
                      flexDirection="column"
                      alignItems="start"
                      w={"full"}
                    >
                      <HStack>
                        <Text color="gray_description">Prénoms du représentant</Text>
                        <span style={{ color: "red" }}>*</span>
                      </HStack>
                      <FormControl
                        isInvalid={
                          !!errors.firstName && (touched.firstName as boolean)
                        }
                      >
                        <AppInput
                          required
                          placeholder={`Prénoms`}
                          borderWidth="1px"
                          alignSelf="stretch"
                          borderRadius="8px"
                          id="firstName"
                          name="firstName"
                          autoComplete="firstName"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.firstName}
                          showErrorMessage={Boolean(
                            touched.firstName && errors.firstName
                          )}
                          errorMessage={errors.firstName}
                        />
                      </FormControl>
                    </Flex>
                  </Flex>
                  <Flex
                    flexDirection={{ md: "row", base: "column" }}
                    w={"full"}
                    gap={5}
                  >
                    <Flex
                      gap="4px"
                      flexDirection="column"
                      alignItems="start"
                      w={"full"}
                    >
                      <HStack>
                        <Text color="gray_description">Email</Text>
                        <span style={{ color: "red" }}>*</span>
                      </HStack>
                      <FormControl
                        isInvalid={!!errors.email && (touched.email as boolean)}
                      >
                        <AppInput
                          required
                          placeholder={`ronald.koffikra@gmail.com`}
                          type="email"
                          borderWidth="1px"
                          alignSelf="stretch"
                          borderRadius="8px"
                          id="email"
                          name="email"
                          autoComplete="email"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.email}
                          showErrorMessage={Boolean(
                            touched.email && errors.email
                          )}
                          errorMessage={errors.email}
                        />
                      </FormControl>
                    </Flex>
                    <Flex
                      gap="4px"
                      flexDirection="column"
                      alignItems="start"
                      w={"full"}
                    >
                      <HStack>
                        <Text color="gray_description">
                          Numéro de téléphone
                        </Text>
                        <span style={{ color: "red" }}>*</span>
                      </HStack>
                      <PhoneNumberInput
                        name="phoneNumber"
                        onChange={setFieldValue}
                        value={values.phoneNumber}
                        countryDialCode={taxPayer?.country_dial_code}
                        onBlur={handleBlur}
                        showErrorMessage={Boolean(
                          touched.phoneNumber && errors.phoneNumber
                        )}
                        errorMessage={errors.phoneNumber}
                      />
                    </Flex>
                  </Flex>
                  {/* Champs de mot de passe (mode création et modification) */}
                  <Flex
                    gap="4px"
                    bg="white"
                    boxShadow="xl"
                    w="100%"
                    flexDirection="column"
                    px={{ base: "20px", sm: "24px" }}
                    py={{ base: "20px", sm: "24px" }}
                    mx={{ md: "0px", base: "0px" }}
                    borderRadius="16px"
                    mb="18px"
                  >
                    <Flex gap="12px" flexDirection="column">
                      <Heading size="md" fontFamily="Arimo">
                        Mot de passe
                      </Heading>

                      {/* Checkbox pour masquer/afficher les champs de mot de passe */}
                      <Flex alignItems="center" gap="8px">
                        <input
                          type="checkbox"
                          id="showPasswordFields"
                          checked={showPasswordFields}
                          onChange={(e) => {
                            setShowPasswordFields(e.target.checked);
                            if (!e.target.checked) {
                              // Réinitialiser les champs si masqués
                              setFieldValue("password", "");
                              setFieldValue("passwordConfirmation", "");
                            }
                          }}
                          style={{ width: "18px", height: "18px", cursor: "pointer" }}
                        />
                        <Text
                          as="label"
                          htmlFor="showPasswordFields"
                          color="gray_description"
                          cursor="pointer"
                        >
                          {id ? "Modifier le mot de passe" : "Définir un mot de passe personnalisé"}
                        </Text>
                      </Flex>

                      {/* Champs de mot de passe (affichés seulement si checkbox activée) */}
                      {showPasswordFields && (
                        <Flex gap="12px" flexDirection="column">
                          <Flex gap="4px" flexDirection="column" alignItems="start" w={"full"}>
                            <HStack>
                              <Text color="gray_description">Mot de passe</Text>
                            </HStack>
                            <FormControl
                              isInvalid={!!errors.password && (touched.password as boolean)}
                            >
                              <AppInput
                                placeholder="Mot de passe"
                                borderWidth="1px"
                                alignSelf="stretch"
                                borderRadius="8px"
                                id="password"
                                name="password"
                                type="password"
                                autoComplete="new-password"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.password}
                                showErrorMessage={Boolean(touched.password && errors.password)}
                                errorMessage={errors.password}
                              />
                            </FormControl>
                          </Flex>

                          <Flex gap="4px" flexDirection="column" alignItems="start" w={"full"}>
                            <HStack>
                              <Text color="gray_description">Confirmation du mot de passe</Text>
                            </HStack>
                            <FormControl
                              isInvalid={!!errors.passwordConfirmation && (touched.passwordConfirmation as boolean)}
                            >
                              <AppInput
                                placeholder="Confirmer le mot de passe"
                                borderWidth="1px"
                                alignSelf="stretch"
                                borderRadius="8px"
                                id="passwordConfirmation"
                                name="passwordConfirmation"
                                type="password"
                                autoComplete="new-password"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.passwordConfirmation}
                                showErrorMessage={Boolean(touched.passwordConfirmation && errors.passwordConfirmation)}
                                errorMessage={errors.passwordConfirmation}
                              />
                            </FormControl>
                          </Flex>

                          <Text fontSize="12px" color="gray.500">
                            {id
                              ? "Si aucun mot de passe n'est défini, le mot de passe existant sera conservé."
                              : "Si aucun mot de passe n'est défini, un mot de passe sera généré automatiquement."
                            }
                          </Text>
                        </Flex>
                      )}
                    </Flex>
                  </Flex>
                  <Flex
                    flexDirection={{ md: "row", base: "column" }}
                    w={"full"}
                    gap={5}
                  >
                    {/* Latitude */}
                    <Flex
                      gap="4px"
                      flexDirection="column"
                      alignItems="start"
                      w={"full"}
                    >
                      <HStack>
                        <Text color="gray_description">Latitude</Text>
                        <Icon as={MdMyLocation as any} boxSize={4} onClick={handleGetLocation} />
                      </HStack>
                      <FormControl
                        isInvalid={!!errors.lat && (touched.lat as boolean)}
                      >
                        <AppInput
                          placeholder={`Latitude`}
                          borderWidth="1px"
                          alignSelf="stretch"
                          borderRadius="8px"
                          id="lat"
                          name="lat"
                          autoComplete="lat"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.lat}
                          showErrorMessage={Boolean(touched.lat && errors.lat)}
                          errorMessage={errors.lat}
                        />
                      </FormControl>
                    </Flex>

                    {/* Longitude */}
                    <Flex
                      gap="4px"
                      flexDirection="column"
                      alignItems="start"
                      w={"full"}
                    >
                      <HStack>
                        <Text color="gray_description">Longitude</Text>
                        <Icon as={MdMyLocation as any} boxSize={4} onClick={handleGetLocation} />
                      </HStack>
                      <FormControl
                        isInvalid={!!errors.long && (touched.long as boolean)}
                      >
                        <AppInput
                          placeholder={`Longitude`}
                          borderWidth="1px"
                          alignSelf="stretch"
                          borderRadius="8px"
                          id="long"
                          name="long"
                          autoComplete="long"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.long}
                          showErrorMessage={Boolean(
                            touched.long && errors.long
                          )}
                          errorMessage={errors.long}
                        />
                      </FormControl>
                    </Flex>
                  </Flex>
                  <Flex
                    flexDirection={{ md: "row", base: "column" }}
                    w={"full"}
                    gap={5}
                  >

                    {/* Pays - Masqué pour les tax collectors */}
                    {!isTaxCollector && (userRole === TAX_SUPER_ADMIN) && (
                      <Flex
                        gap="4px"
                        flexDirection="column"
                        alignItems="start"
                        w={"full"}
                      >
                        <HStack>
                          <Text color="gray_description">Pays</Text>
                          <span style={{ color: "red" }}>*</span>
                        </HStack>
                        <FormControl
                          isInvalid={!!errors.country && (touched.country as boolean)}
                        ></FormControl>
                        {!isCountryLoading && (
                          <AppSelect
                            id="country"
                            name="country"
                            onChange={(field: string, value: any) => {
                              setFieldValue(field, value);
                              setFieldValue("city", "");
                              setFieldValue("area", "");
                              setFieldValue("subArea", "");
                              // Charger les villes du pays sélectionné
                              if (value) {
                                mutateCity(getCities(value || "", 1, ""));
                              } else {
                                mutateCity([]); // Vider les villes si aucun pays
                              }
                            }}
                            value={values.country || initialValues.country}
                            placeholder="Veuillez sélectionner un pays"
                            options={countries}
                            isMulti={false}
                            isLoading={isCountryLoading}
                            showErrorMessage={Boolean(
                              touched.country && errors.country
                            )}
                            errorMessage={errors.country}
                            onBlur={handleBlur}
                          />
                        )}
                      </Flex>
                    )}

                    {/* Ville - Masquée pour les tax collectors */}
                    {!isTaxCollector && (userRole === TAX_SUPER_ADMIN) && (
                      <Flex
                        gap="4px"
                        flexDirection="column"
                        alignItems="start"
                        w={"full"}
                      >
                        <HStack>
                          <Text color="gray_description">Ville</Text>
                          <span style={{ color: "red" }}>*</span>
                        </HStack>
                        <FormControl
                          isInvalid={!!errors.city && (touched.city as boolean)}
                        ></FormControl>
                        {!isCityLoading && (
                          <AppSelect
                            id="city"
                            name="city"
                            handleInputChange={(value) => {
                              setCity(value)
                              mutateCity(getCities(0, 1, value));
                            }}
                            onChange={(field: string, value: any) => {
                              setFieldValue(field, value);
                              setFieldValue("subArea", "");
                              setFieldValue("area", "");
                              mutateArea(getAreas(0, value, ""));
                              mutateSubArea(getSubAreas(0, value, "", institution));
                            }}
                            value={values.city || initialValues.city}
                            placeholder="Veuillez sélectionner un pays"
                            options={cities}
                            isMulti={false}
                            isLoading={isCityLoading}
                            showErrorMessage={Boolean(
                              touched.city && errors.city
                            )}
                            errorMessage={errors.city}
                            onBlur={handleBlur}
                          />
                        )}
                      </Flex>
                    )}

                    {/* Commune - Masquée pour les tax collectors */}
                    {!isTaxCollector && (userRole === TAX_SUPER_ADMIN) && (
                      <Flex
                        gap="4px"
                        flexDirection="column"
                        alignItems="start"
                        w={"full"}
                      >
                        <HStack>
                          <Text color="gray_description">Commune</Text>
                          <span style={{ color: "red" }}>*</span>
                        </HStack>
                        <FormControl
                          isInvalid={!!errors.area && (touched.area as boolean)}
                        ></FormControl>
                        {!isAreaLoading && (
                          <AppSelect
                            id="area"
                            name="area"
                            onChange={(field: string, value: any) => {
                              const val = value || "";
                              setFieldValue(field, val);
                              setFieldValue("subArea", "");
                              mutateArea(getAreas(1, val));
                              console.log(val)
                              mutateSubArea(getSubAreas(1, String(val), "", institution));
                            }}
                            value={values.area || initialValues.area}
                            placeholder="Veuillez sélectionner une commune"
                            options={areas}
                            isMulti={false}
                            isLoading={isAreaLoading}
                            showErrorMessage={Boolean(touched.area && errors.area)}
                            errorMessage={errors.area}
                            onBlur={handleBlur}
                          />
                        )}
                      </Flex>
                    )}

                  </Flex>


                  {/* Zone - Masquée pour les tax collectors */}
                  {!isTaxCollector && (userRole === TAX_SUPER_ADMIN) && (
                    <Flex
                      flexDirection={{ md: "row", base: "column" }}
                      w={"full"}
                      gap={5}
                    >
                      <Flex
                        gap="4px"
                        flexDirection="column"
                        alignItems="start"
                        w={"full"}
                      >
                        <HStack>
                          <Text color="gray_description">Zone</Text>
                          <span style={{ color: "red" }}>*</span>
                        </HStack>
                        <FormControl
                          isInvalid={
                            !!errors.subArea && (touched.subArea as boolean)
                          }
                        ></FormControl>
                        {!isSubAreaLoading && (
                          <AppSelect
                            id="subArea"
                            name="subArea"
                            onChange={(field: string, value: any) => {
                              setFieldValue(field, value);
                            }}
                            value={values.subArea || initialValues.subArea}
                            placeholder="Veuillez sélectionner une zone"
                            options={subAreas}
                            isMulti={false}
                            isLoading={isSubAreaLoading}
                            showErrorMessage={Boolean(touched.subArea && errors.subArea)}
                            errorMessage={errors.subArea}
                            onBlur={handleBlur}
                          />
                        )}
                      </Flex>
                      <Flex
                        gap="4px"
                        flexDirection="column"
                        alignItems="start"
                        w={"full"}
                        mt={1}
                      >
                        <HStack>
                          <Text color="gray_description">Point de repère</Text>
                        </HStack>
                        <FormControl
                          isInvalid={
                            !!errors.landmark && (touched.landmark as boolean)
                          }
                        >
                          <AppInput
                            placeholder={`Point de repère`}
                            borderWidth="1px"
                            alignSelf="stretch"
                            borderRadius="8px"
                            id="landmark"
                            name="landmark"
                            autoComplete="landmark"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            value={values.landmark}
                            showErrorMessage={Boolean(
                              touched.landmark && errors.landmark
                            )}
                            errorMessage={errors.landmark}
                          />
                        </FormControl>
                      </Flex>
                    </Flex>
                  )}


                  {/* Description de l'adresse */}
                  <Flex
                    gap="4px"
                    flexDirection="column"
                    alignItems="start"
                    w={"full"}
                    mt={1}
                  >
                    <HStack>
                      <Text color="gray_description">Description de l'adresse</Text>
                    </HStack>
                    <FormControl
                      isInvalid={
                        !!errors.addressDescription &&
                        (touched.addressDescription as boolean)
                      }
                    >
                      <AppTextArea
                        placeholder={`Description de l'adresse`}
                        borderWidth="1px"
                        alignSelf="stretch"
                        borderRadius="8px"
                        id="addressDescription"
                        name="addressDescription"
                        autoComplete="addressDescription"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={
                          values.addressDescription ||
                          initialValues.addressDescription
                        }
                        showErrorMessage={Boolean(
                          touched.addressDescription &&
                          errors.addressDescription
                        )}
                        errorMessage={errors.addressDescription}
                      />
                    </FormControl>
                  </Flex>
                </Flex>
              </Flex>
              <Flex mt="24px" gap="12px">
                <Button
                  size="4xl"
                  color="gray_description"
                  fontFamily="Arimo"
                  fontWeight={700}
                  minW="106px"
                  borderRadius="8px"
                  onClick={() => window.history.back()}
                >
                  Annuler
                </Button>
                <Button
                  size="4xl"
                  colorScheme="red_origin"
                  fontFamily="Arimo"
                  fontWeight={700}
                  minW="132px"
                  borderRadius="8px"
                  type="submit"
                  isLoading={isSubmitting}
                >
                  Enregistrer
                </Button>
              </Flex>
            </Flex>
          </form>
        </Flex>
      )}
    </>
  );
}
